/**
 * The physical scorecard, photographed, one tap away.
 *
 * The app's own card view is the DERIVED card — the numbers the engine scores
 * from. This is the artefact those numbers were checked against, and it answers a
 * different question: not "what does the app think the 7th is" but "what does the
 * card in the pro shop say". When those two ever disagree mid-argument, this is
 * the exhibit, which is why it is reachable from the scoring screen and not just
 * from Setup.
 *
 * The images ship with the app (public/cards/<courseId>.webp) and are PRECACHED
 * by the service worker at install — not merely runtime-cached, which is what
 * the first version relied on and what the audit caught: a card never opened
 * online was a broken image at exactly the moment it was reached for. Adding a
 * course here means adding its file to the precache list in public/sw.js; the
 * sw test names each file, so forgetting fails loudly.
 */

import { useEffect, useState } from 'react'

/*
 * Which courses have a photograph, by id.
 *
 * A hardcoded list rather than probing for the file, deliberately: a probe turns
 * a missing image into a runtime surprise, and this way adding a photo is two
 * steps (drop the file, add the id) that fail loudly in review if half-done. The
 * Idaho courses have no photos and should not offer a button that opens a 404.
 */
const CARD_PHOTOS = new Set([
  'bandon-dunes',
  'pacific-dunes',
  'bandon-trails',
  'old-macdonald',
  'sheep-ranch',
])

export const hasCardPhoto = (courseId: string): boolean => CARD_PHOTOS.has(courseId)

/*
 * Zoom is stepped, not pinched.
 *
 * In standalone mode iOS disables page pinch-zoom, so a "just pinch it" overlay
 * works in the browser and silently does not on the twelve home-screen installs
 * this app is actually used from. Steps sidestep that: tap the button (or the
 * image) to cycle, drag to pan — plain overflow scrolling, which works
 * everywhere. 1x fits the card's width to the screen; 2.5x makes a yardage cell
 * legible; 4x is for arguing about a single digit.
 */
const ZOOM_STEPS = [1, 2.5, 4]

export function CardPhotoOverlay({
  courseId,
  courseName,
  onClose,
}: {
  courseId: string
  courseName: string
  onClose: () => void
}) {
  const [zoom, setZoom] = useState(0)

  // The page behind must not scroll while the card is up — on iOS the two
  // scroll containers otherwise fight, and the overlay loses.
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  const step = () => setZoom((z) => (z + 1) % ZOOM_STEPS.length)

  return (
    <div className="fixed inset-0 z-50 bg-ink flex flex-col">
      <div className="flex items-center gap-3 px-4 py-3 pt-safe text-paper">
        <span className="bc-label flex-1 truncate">{courseName} — the card</span>
        <button
          type="button"
          onClick={step}
          className="bc-tap bc-label border-2 border-paper px-3 py-1.5"
        >
          {ZOOM_STEPS[zoom]}x
        </button>
        <button
          type="button"
          onClick={onClose}
          className="bc-tap bc-label border-2 border-paper px-3 py-1.5"
        >
          Close
        </button>
      </div>

      {/*
        The scroll container IS the zoom mechanism: the image is laid out at
        zoom x screen-width and the container pans over it natively. No
        transforms, no gesture code, nothing that can disagree with the
        browser about where a touch was headed.
      */}
      <div className="flex-1 overflow-auto overscroll-contain">
        <img
          src={`/cards/${courseId}.webp`}
          alt={`${courseName} scorecard`}
          onClick={step}
          className="max-w-none"
          style={{ width: `${ZOOM_STEPS[zoom]! * 100}vw` }}
          draggable={false}
        />
      </div>

      <p className="px-4 py-2 pb-safe text-[12px] text-onink text-center">
        Tap the card to zoom. Drag to move around it.
      </p>
    </div>
  )
}

/**
 * The way in, sized to sit inside an existing control row without being one
 * more shouting button. Renders nothing for a course with no photograph.
 */
export function CardPhotoButton({
  courseId,
  courseName,
}: {
  courseId: string
  courseName: string
}) {
  const [open, setOpen] = useState(false)
  if (!hasCardPhoto(courseId)) return null
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="bc-tap bc-label border border-ink px-2.5 shrink-0"
      >
        Card
      </button>
      {open && (
        <CardPhotoOverlay
          courseId={courseId}
          courseName={courseName}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}
